import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useDocumentModalScrollLock } from "@/hooks/use-document-modal-scroll-lock";

type ImagePreview = {
  src: string;
  alt: string;
};

function isExcludedImage(image: HTMLImageElement): boolean {
  if (!image.currentSrc && !image.src) return true;
  if (image.closest("[data-image-fullscreen='off']")) return true;
  if (image.closest("nav, [role='navigation']")) return true;

  const alt = image.alt.trim().toLowerCase();
  return alt.includes("logo") || alt.includes("avatar");
}

export function FullscreenImageViewer() {
  const [preview, setPreview] = useState<ImagePreview | null>(null);

  useEffect(() => {
    const handleImageClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLImageElement) || isExcludedImage(target)) {
        return;
      }

      const src = target.currentSrc || target.src;
      if (!src) return;

      event.preventDefault();
      event.stopPropagation();
      setPreview({ src, alt: target.alt || "Image preview" });
    };

    document.addEventListener("click", handleImageClick, true);
    return () => document.removeEventListener("click", handleImageClick, true);
  }, []);

  useDocumentModalScrollLock(Boolean(preview));

  return (
    <DialogPrimitive.Root
      open={Boolean(preview)}
      onOpenChange={(open) => {
        if (!open) setPreview(null);
      }}
    >
      {preview ? (
        <DialogPrimitive.Portal>
          <DialogPrimitive.Content
            className="fixed inset-0 z-layer-fullscreen flex pointer-events-auto items-center justify-center bg-black/90 p-2 outline-none sm:p-5"
            aria-describedby={undefined}
            onClick={() => setPreview(null)}
          >
            <DialogPrimitive.Title className="sr-only">
              {preview.alt}
            </DialogPrimitive.Title>

            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute right-3 top-3 z-layer-fullscreen-chrome pointer-events-auto rounded-full shadow-lg sm:right-5 sm:top-5"
                aria-label="Close full screen image"
                onClick={(event) => event.stopPropagation()}
              >
                <X className="h-5 w-5" />
              </Button>
            </DialogPrimitive.Close>

            <img
              src={preview.src}
              alt={preview.alt}
              className="max-h-[calc(100dvh-1rem)] max-w-[calc(100vw-1rem)] select-none object-contain sm:max-h-[calc(100dvh-2.5rem)] sm:max-w-[calc(100vw-2.5rem)]"
              onClick={(event) => event.stopPropagation()}
              data-image-fullscreen="off"
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      ) : null}
    </DialogPrimitive.Root>
  );
}
